import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * What the customer sees when the application itself breaks.
 *
 * WHY THIS EXISTS
 *   A React render error unmounts the tree below it. The page keeps its last painted pixels, so nothing
 *   looks wrong - but every button is gone from the DOM's point of view and every click does nothing at
 *   all. The customer taps a choice, taps it again, and concludes the option "is not clickable"; there is
 *   no error, no spinner and no message, and the only evidence is in a console they will never open.
 *
 *   That is the worst shape a failure can take, and it happened twice during this project's own testing.
 *   A boundary turns it into something a customer can act on and something a developer can be told about.
 *
 * WHAT IT DOES NOT DO
 *   It does not clear the sale. The draft lives in localStorage and is deliberately left alone, so
 *   reloading returns the customer to the device and answers they had already given. Offering "start
 *   again" as the only way out of a crash would throw away work the crash did not actually destroy.
 */
interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console for whoever is debugging. There is no error-reporting service wired up here,
    // and inventing one silently would send a customer's conversation somewhere nobody agreed to.
    console.error('SellGPT crashed:', error, info.componentStack)
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children

    return (
      <div className="crash" role="alert">
        <h2>Something went wrong</h2>
        <p>
          The page stopped responding. Your sale is saved — reloading will bring back the device and
          answers you had already given.
        </p>
        <div className="row">
          <button type="button" className="primary" onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <a className="secondary" href="/" style={{ textDecoration: 'none' }}>
            Start again
          </a>
        </div>
        <p className="hint">
          If it keeps happening, tell Cashit support what you were selling and what you last tapped.
        </p>
      </div>
    )
  }
}
